import { Component } from '@angular/core';
import { AuthService, DialogService, Navigator, tr, KeyBase, DialogResult } from '@kpd/framework-ngx';
import { UserFacade } from 'app/shared/services/facades/user.facade';
import { User } from 'app/shared/models/user';
import { LicenseService } from 'app/shared/services/license.service';
import { Tenant } from 'app/shared/models/tenant';
import { getAuthProviderName } from 'app/shared/enums/authentication-providers';
import { UsersNavigatorHelper } from 'app/users/users-navigator-helper';
import { Permissions } from 'app/shared/services/permissions';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { UMLocalizationService } from 'app/shared/services/localization.service';

@Component({
  selector: 'app-users-sidepanel',
  templateUrl: './users-sidepanel.component.html',
})
export class UserSidePanelComponent {
  Permissions: typeof Permissions = Permissions;
  navigatorHelper: UsersNavigatorHelper;
  tenant: Tenant = new Tenant();
  userLicensesCount = 0;
  userRoleCount = 0;
  currentKey: KeyBase;

  get hasSystemUserManagePermission(): boolean {
    return this.authService.getIdentity().hasPermission(Permissions.SystemUserManage);
  }

  get isSystemUserSelected(): Observable<boolean> {
    return this.userFacade.selectedItems$.pipe(map((items) => items.some((i) => i.isSystemUser)));
  }

  constructor(
    public userFacade: UserFacade,
    private navigator: Navigator,
    private dialogService: DialogService,
    private licenseService: LicenseService,
    private authService: AuthService,
  ) {
    this.navigatorHelper = new UsersNavigatorHelper(navigator);
  }

  onItemChange(item: User): void {
    this.currentKey = item.key;
    if (!!item && !!item.key && item.key.isDefined()) {
      this.currentKey = item.key;
      const activeTenantId = this.authService.getTenant().key.id;
      this.licenseService.getLicensesForUser(item.upn as string).subscribe((licenses) => {
        this.userRoleCount = item.getRolesCount(activeTenantId);
        this.userLicensesCount = licenses.filter((l) => !l.isExpired).length;
      });
    }
  }

  onAdd(): void {
    this.userFacade.clearSelection();
    this.navigatorHelper.navigateToAdd(false, false);
  }

  onView(userId: string): void {
    this.navigatorHelper.navigateToConsult(userId);
  }

  onEdit(userId: string): void {
    this.navigatorHelper.navigateToEdit(userId);
  }

  onDelete(selectedIds: string[]): void {
    const message = tr('Are you sure to delete one (or more) selected users?');
    this.dialogService
      .open('delete', {
        message,
        header: tr('Delete user(s)'),
        verifyButtonText: tr('Delete'),
        verifyButtonStyle: 'danger',
        cancelButtonText: tr('Cancel'),
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === DialogResult.Verify) {
          this.userFacade.deleteItems(selectedIds).subscribe();
        }
      });
  }

  navigateToLicenses(id: string): void {
    this.navigator.navigateChild(['users', 'detail', id], { queryParams: { tab: 'licenses' } });
  }
  navigateToRoles(id: string): void {
    this.navigator.navigateChild(['users', 'detail', id], { queryParams: { tab: 'roles' } });
  }

  getCultureByCultureString(culture: string): string {
    return UMLocalizationService.getCultureDisplay(culture) || tr('Unsupported') + (culture ? ` (${culture})` : '');
  }

  getAuthProviders(user: User): string[] {
    return getAuthProviderName(user.allowedAuthenticationProviders || 0).map((name) => tr(name));
  }

  canChangePassword(user: User): boolean {
    if (!user.key) {
      return false;
    }
    return user.key.toString() === this.authService.getIdentity().upn && user.isLocal();
  }

  changePassword(): void {
    this.dialogService
      .open('changePassword', {
        header: tr('Change password'),
        verifyButtonText: tr('Change'),
        cancelButtonText: tr('Cancel'),
        userKey: this.currentKey,
      })
      .afterClosed()
      .subscribe();
  }

  reloadData(): void {
    this.userFacade.reloadData();
  }
}
